import { useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export const useFootballRotation = (mainRef) => {
  useEffect(() => {
    const footballImage = document.getElementById('football')
    let deg = 0
    let lastScrollTop = 0

    const handleRotate = () => {
      footballImage.style.transform = `rotate(${deg}deg)`
      let st = window.pageYOffset || document.documentElement.scrollTop
      if (st > lastScrollTop) {
        deg += 20
      } else if (st < lastScrollTop) {
        deg -= 20
      }

      lastScrollTop = st <= 0 ? 0 : st
    }

    window.addEventListener('scroll', handleRotate)

    return () => {
      window.removeEventListener('scroll', handleRotate)
    }
  }, [mainRef])
}

export const useBouncingEffect = (mainRef) => {
  const bouncingAnim = document.getElementsByClassName('bouncingAnim')
  useEffect(() => {
    const tl = gsap.timeline({ repeat: -1, yoyo: true })
    tl.from(bouncingAnim, {
      y: -20,
      scale: 1,
      ease: 'none',
    }).to(bouncingAnim, {
      y: 30,
      scale: 0.5,
      ease: 'none',
    })
  }, [mainRef])
}

export const useHomePageAnimation = (mainRef) => {
  useEffect(() => {
    const mainElement = mainRef.current

    if (!mainElement) return

    // Refresh ScrollTrigger positions
    const refreshTriggers = () => {
      ScrollTrigger.refresh()
    }

    window.addEventListener('load', refreshTriggers)
    window.addEventListener('resize', refreshTriggers)

    const headerTimeline = gsap.timeline({ onComplete: refreshTriggers })

    headerTimeline
      .fromTo(
        '.header',
        {
          opacity: 0,
          y: -30,
        },
        {
          opacity: 1,
          y: 0,
        }
      )
      .fromTo(
        '.header .logo',
        {
          opacity: 0,
          y: -30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.1,
        }
      )
      .fromTo(
        '.header .NavMenus',
        {
          opacity: 0,
          y: -30,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.1,
        }
      )
      .fromTo(
        '.hero-section .netImg',
        {
          opacity: 0,
          y: -30,
        },
        {
          opacity: 1,
          y: 0,
          ease: 'none',
          duration: 0.4,
        }
      )
      .fromTo(
        '.hero-section .hero-heading',
        {
          opacity: 0,
          x: -20,
          skewX: 10,
          ease: 'none',
        },
        {
          opacity: 1,
          x: 0,
          skewX: 0,
          ease: 'none',
          duration: 0.2,
        }
      )
      .fromTo(
        '.hero-section .para',
        {
          opacity: 0,
          x: -30,
          ease: 'none',
        },
        {
          opacity: 1,
          x: 0,
          ease: 'none',
          duration: 0.2,
        }
      )
      .fromTo(
        '.football-container',
        {
          opacity: 0,
          y: 30,
          ease: 'none',
        },
        {
          opacity: 1,
          y: 0,
          ease: 'none',
          duration: 0.2,
        }
      )

    gsap.utils.toArray('.animateJourneyText').forEach((ele) => {
      gsap.fromTo(
        ele,
        {
          opacity: 0,
          y: 100,
          skewX: 10,
        },
        {
          opacity: 1,
          y: 0,
          skewX: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: ele,
            start: 'top 80%',
            end: 'top 60%',
            toggleActions: 'play none none none',
          },
          duration: 0.2,
        }
      )
    })

    gsap.utils.toArray('.animateJourneyImg').forEach((ele) => {
      gsap.fromTo(
        ele,
        {
          opacity: 0,
          y: 30,
          scale: 0,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          ease: 'none',
          scrollTrigger: {
            trigger: ele,
            start: 'top 100%',
            end: 'bottom 100%',
            toggleActions: 'play none none none',
          },
          duration: 0.2,
        }
      )
    })

    //  why section animation
    const sectionTitle = mainRef.current.querySelector(
      '.whySection .section-title'
    )

    // whySection animation
    const oneAnim = mainRef.current.querySelector('.whySection .oneAnim')
    const twoAnim = mainRef.current.querySelector('.whySection .twoAnim')
    const threeAnim = mainRef.current.querySelector('.whySection .threeAnim')
    const textElements = gsap.utils.toArray('.whySection h3, .whySection p')

    const whySectionTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.whySection',
        start: 'top 100%',
        end: 'bottom 100%',
        toggleActions: 'play none none none',
      },
      duration: 0.2,
    })

    // Animate section-title
    whySectionTimeline.fromTo(
      sectionTitle,
      {
        y: -30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    // Animate oneAnim
    whySectionTimeline.fromTo(
      oneAnim,
      {
        x: -30,
        opacity: 0,
      },
      {
        x: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    // Animate twoAnim
    whySectionTimeline.fromTo(
      twoAnim,
      {
        y: 20,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      },
      '-=0.5' // Overlap the previous animation by 0.5 seconds
    )

    // Animate threeAnim
    whySectionTimeline.fromTo(
      threeAnim,
      {
        x: 30,
        opacity: 0,
      },
      {
        x: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      },
      '-=0.5' // Overlap the previous animation by 0.5 seconds
    )

    // Animate h3 and p elements inside whySection
    textElements.forEach((ele) => {
      whySectionTimeline.fromTo(
        ele,
        {
          y: -30,
        },
        {
          y: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: ele,
            start: 'top 90%',
            end: 'bottom 100%',
            toggleActions: 'play none none none',
          },
          duration: 0.2,
        }
      )
    })

    // where fans play section animation
    const whereSectionTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.main-play-area',
        start: 'top 90%',
        end: 'bottom 100%',
        toggleActions: 'play none none none',
        duration: 0.2,
      },
    })

    whereSectionTimeline.fromTo(
      '.main-play-area .section-title',
      {
        y: -30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    whereSectionTimeline.fromTo(
      '.main-play-area .play-one',
      {
        y: -30,
        x: -30,
        opacity: 0,
      },
      {
        y: 0,
        x: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )
    whereSectionTimeline.fromTo(
      '.main-play-area .play-two',
      {
        y: 30,
        x: -30,
        opacity: 0,
      },
      {
        y: 0,
        x: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    whereSectionTimeline.fromTo(
      '.main-play-area .play-three',
      {
        y: -30,
        x: 30,
        opacity: 0,
      },
      {
        y: 0,
        x: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )
    whereSectionTimeline.fromTo(
      '.main-play-area .play-four',
      {
        y: 30,
        x: 30,
        opacity: 0,
      },
      {
        y: 0,
        x: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    // supported by section ==================

    const supportedSectionTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.support-main-section',
        start: 'top 90%',
        end: 'bottom 100%',
        toggleActions: 'play none none none',
        duration: 0.2,
      },
    })

    supportedSectionTimeline.fromTo(
      '.support-main-section .section-title',
      {
        y: -30,
        opacity: 0,
      },
      {
        y: 0,
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    const supportImgs = gsap.utils.toArray(
      '.support-main-section .support-image-area img'
    )

    supportImgs.forEach((ele) => {
      supportedSectionTimeline.fromTo(
        ele,
        {
          y: 30,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          ease: 'none',
          duration: 0.2,
        }
      )
    })

    supportedSectionTimeline.fromTo(
      '.fantacy-sport-section h2',
      {
        opacity: 0,
      },
      {
        opacity: 1,
        ease: 'none',
        duration: 0.2,
      }
    )

    // footer animation ============================
    const footerTimeline = gsap.timeline({
      scrollTrigger: '.footer-main-section',
      start: 'bottom 100%',
      toggleActions: 'play none none none',
    })

    footerTimeline.fromTo(
      '.footer-main-section .footer-logo',
      {
        opacity: 0,
        skewX: 20,
      },
      {
        opacity: 1,
        skewX: 0,
        ease: 'none',
        duration: 0.2,
      }
    )
    footerTimeline.fromTo(
      '.footer-main-section .footer-icon-container',
      {
        opacity: 0,
        skewX: 20,
      },
      {
        opacity: 1,
        skewX: 0,
        ease: 'none',
        duration: 0.2,
      }
    )
    footerTimeline.fromTo(
      '.footer-main-section .footer-copyright-text',
      {
        opacity: 0,
        skewX: 20,
      },
      {
        opacity: 1,
        skewX: 0,
        ease: 'none',
        duration: 0.2,
      }
    )

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener('load', refreshTriggers)
      window.removeEventListener('resize', refreshTriggers)
    }
  }, [mainRef])
}
